import { useEffect } from 'react';
import { Button, Checkbox } from '@uclaea/react-component-library';
import { AttendanceScope, AttendeeRole, Registration } from '../API';
import { usePartyAttendance } from '../hooks/usePartyAttendance';
import { activePeople } from '../utils/partyAttendance';
import { formatDate } from '../utils/formatDate';
import '../css/PartyAttendance.css';

// Return rows for the existing Registrants table; a party remains one pagination unit.
export function PartyAttendanceRows({ party, notes = '', onChange, selected = false, onSelect, bulkBusy = false, visible = true, onRegisterCheckIn }: {
  party: Registration; notes?: string; onChange: (party: Registration) => void;
  selected?: boolean; onSelect?: () => void; bulkBusy?: boolean; visible?: boolean;
  onRegisterCheckIn?: (id: string, checkIn: (() => Promise<boolean>) | null) => void;
}) {
  const { run, retry, busy, pending, error } = usePartyAttendance(party, onChange);
  // Keep this command controller mounted across filtering/pagination so bulk actions
  // share the same pending request and retry controls as person-level actions.
  useEffect(() => {
    onRegisterCheckIn?.(party.registrationId, () => run({ scope: AttendanceScope.PARTY, checkedIn: true }));
    return () => onRegisterCheckIn?.(party.registrationId, null);
  }, [onRegisterCheckIn, party.registrationId, run]);
  if (!visible) return null;
  const selection = <Checkbox label="" checked={selected} onChange={onSelect} disabled={bulkBusy}
    aria-label={`Select registration for ${party.firstName ?? ''} ${party.lastName ?? ''}`} />;
  const people = activePeople(party);
  const disabled = bulkBusy || busy || !!pending || ['CANCELLED', 'DELETED'].includes(party.registrationStatus);
  const primary = people.find(person => person.role === AttendeeRole.PRIMARY);
  const date = party.dateCreated && Number.isFinite(Date.parse(party.dateCreated)) ? formatDate(party.dateCreated) : '—';
  const groupButton = <Button variant="secondary" label="Check in entire group"
    disabled={disabled || !people.some(p => !p.isCheckedIn)}
    onClick={() => void run({ scope: AttendanceScope.PARTY, checkedIn: true })} />;
  const total = `${party.checkedInCount ?? 0} / ${party.guests ?? 0}`;
  return <>
    {!primary && <tr className="attendee-row party-contact">
      <td>{selection}</td>
      <td>{party.firstName} {party.lastName}<small>Registration contact (not attending)</small></td>
      <td>{party.email || '—'}</td><td className="hide-for-mobile">{date}</td>
      <td>{party.registrationStatus === 'CANCELLED' ? 'Cancelled' : 'Contact only'}</td>
      <td className="hide-for-mobile">{total}</td><td className="hide-for-mobile">{notes || '—'}</td>
      <td className="party-attendance-actions"><div className="party-action-columns"><span /><span>{groupButton}</span></div></td>
    </tr>}
    {people.map(person => <tr key={person.attendeeId} className={`attendee-row ${person.role === AttendeeRole.GUEST ? 'party-guest' : 'party-primary'}`}>
      <td>{person.role === AttendeeRole.PRIMARY ? selection : <span aria-label="Guest of the registration above">—</span>}</td>
      <td>{person.firstName} {person.lastName}<small>{person.role === AttendeeRole.PRIMARY ? 'Primary registrant' : 'Guest'}{party.live === false ? ' · Test registration' : ''}</small></td>
      <td>{person.email || '—'}</td>
      <td className="hide-for-mobile">{person.role === AttendeeRole.PRIMARY ? date : '—'}</td>
      <td>{party.registrationStatus === 'CANCELLED' ? 'Cancelled' : person.isCheckedIn ? 'Checked in' : 'Not checked in'}</td>
      <td className="hide-for-mobile">{person.role === AttendeeRole.PRIMARY ? total : '—'}</td>
      <td className="hide-for-mobile">{person.role === AttendeeRole.PRIMARY ? notes || '—' : '—'}</td>
      <td className="party-attendance-actions"><div className="party-action-columns">
        <span><Button variant="primary" label={person.isCheckedIn ? 'Undo check-in' : 'Check in'} disabled={disabled}
          onClick={() => void run({ scope: AttendanceScope.ATTENDEE, attendeeId: person.attendeeId, checkedIn: !person.isCheckedIn })} /></span>
        {person.role === AttendeeRole.PRIMARY && <span>{groupButton}</span>}
      </div></td>
    </tr>)}
    {(busy || error || pending) && <tr className="party-feedback"><td colSpan={8}>
      {busy && <span role="status">Saving attendance…</span>}
      {error && <p role="alert">{error}</p>}
      {pending && !busy && <Button variant="secondary" label="Retry last action" disabled={bulkBusy} onClick={() => void retry()} />}
    </td></tr>}
  </>;
}
